import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Loader2 } from 'lucide-react';
import { useSingleFlight } from '../hooks/useSingleFlight';

type ModalSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full';
type ModalVariant = 'default' | 'form' | 'confirm';

export interface StandardModalProps {
  // Core
  open: boolean;
  onClose: () => void;
  title?: string;
  size?: ModalSize;
  variant?: ModalVariant;

  // Content
  children?: React.ReactNode;
  message?: string; // For confirm variant

  // Actions
  onSave?: () => void | Promise<void>; // Form variant
  onConfirm?: () => void | Promise<void>; // Confirm variant

  // Behavior
  closeOnEscape?: boolean;
  closeOnBackdrop?: boolean;

  // State
  isLoading?: boolean;

  // Customization
  confirmText?: string;
  cancelText?: string;
  destructive?: boolean;
  footer?: React.ReactNode;
  hideHeader?: boolean;

  // Styling
  className?: string;
}

const sizeClasses: Record<ModalSize, string> = {
  xs: 'max-w-xs',
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  full: 'max-w-[90vw]',
};

/**
 * StandardModal - Standardized modal component with variants
 *
 * @example
 * // Form variant
 * <StandardModal
 *   variant="form"
 *   title="Edit User"
 *   isLoading={isSaving}
 *   onSave={handleSave}
 *   onClose={handleClose}
 * >
 *   <FormFields />
 * </StandardModal>
 *
 * @example
 * // Confirm variant
 * <StandardModal
 *   variant="confirm"
 *   title="Delete Item"
 *   message="Are you sure?"
 *   destructive
 *   onConfirm={handleDelete}
 *   onClose={handleClose}
 * />
 */
export function StandardModal({
  open,
  onClose,
  title,
  size = 'md',
  variant = 'default',
  children,
  message,
  onSave,
  onConfirm,
  closeOnEscape = true,
  closeOnBackdrop = true,
  isLoading = false,
  confirmText,
  cancelText = 'Cancel',
  destructive = false,
  footer,
  hideHeader = false,
  className = '',
}: StandardModalProps) {
  // Single-flight the action buttons so a spammed confirm/save can't fire the
  // mutation twice. `pending` composes with the caller-supplied isLoading.
  const { pending, run } = useSingleFlight();
  const busy = isLoading || pending;

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && closeOnBackdrop) {
      onClose();
    }
  };

  const handleEscapeKeyDown = (e: KeyboardEvent) => {
    if (!closeOnEscape) {
      e.preventDefault();
    }
  };

  const handlePointerDownOutside = (e: Event) => {
    if (!closeOnBackdrop) {
      e.preventDefault();
    }
  };

  // Determine footer based on variant
  const renderFooter = () => {
    if (footer !== undefined) {
      return footer; // Custom footer overrides variant footer
    }

    if (variant === 'form') {
      return (
        <>
          <button
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300
                       bg-white dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-600
                       rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-700
                       disabled:opacity-50 disabled:cursor-not-allowed
                       focus:outline-hidden focus:ring-2 focus:ring-sky-500"
          >
            {cancelText}
          </button>
          <button
            onClick={() => run(onSave)}
            disabled={busy}
            className="px-4 py-2 text-sm font-medium text-white
                       bg-sky-600 hover:bg-sky-700
                       rounded-lg disabled:opacity-50 disabled:cursor-not-allowed
                       focus:outline-hidden focus:ring-2 focus:ring-sky-500
                       flex items-center gap-2"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirmText || 'Save'}
          </button>
        </>
      );
    }

    if (variant === 'confirm') {
      const confirmButtonClass = destructive
        ? 'bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800'
        : 'bg-sky-600 hover:bg-sky-700';

      return (
        <>
          <button
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300
                       bg-white dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-600
                       rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-700
                       disabled:opacity-50 disabled:cursor-not-allowed
                       focus:outline-hidden focus:ring-2 focus:ring-zinc-500"
          >
            {cancelText}
          </button>
          <button
            onClick={() => run(onConfirm)}
            disabled={busy}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg
                       flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed
                       focus:outline-hidden focus:ring-2 focus:ring-offset-2 ${confirmButtonClass}`}
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirmText || 'Confirm'}
          </button>
        </>
      );
    }

    return null; // Default variant has no automatic footer
  };

  const effectiveSize = variant === 'confirm' && size === 'md' ? 'sm' : size;

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 bg-black/50 data-[state=open]:animate-in
                     data-[state=closed]:animate-out data-[state=closed]:fade-out-0
                     data-[state=open]:fade-in-0 z-[10000]"
        />
        <Dialog.Content
          className={`fixed left-[50%] top-[50%] z-[10000] w-full ${sizeClasses[effectiveSize]}
                     translate-x-[-50%] translate-y-[-50%]
                     bg-white dark:bg-zinc-800 shadow-2xl rounded-lg
                     duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out
                     data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0
                     data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95
                     ${className}`}
          onEscapeKeyDown={handleEscapeKeyDown as never}
          onPointerDownOutside={handlePointerDownOutside}
        >
          {/* Header */}
          {!hideHeader && (
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 dark:border-zinc-700">
              {title && (
                <Dialog.Title className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                  {title}
                </Dialog.Title>
              )}
              <Dialog.Close
                className="ml-auto rounded-xs opacity-70 hover:opacity-100
                           focus:outline-hidden focus:ring-2 focus:ring-zinc-400
                           text-zinc-500 dark:text-zinc-400"
                onClick={onClose}
              >
                <X size={20} />
                <span className="sr-only">Close</span>
              </Dialog.Close>
            </div>
          )}

          {/* Body */}
          <div className="relative px-6 py-4 max-h-[70vh] overflow-y-auto">
            {variant === 'confirm' && message ? (
              <p className="text-base text-zinc-700 dark:text-zinc-300">{message}</p>
            ) : (
              children
            )}

            {/* Loading Overlay */}
            {isLoading && (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events -- loading scrim; swallows clicks while busy. Not an interactive control.
              <div
                className="absolute inset-0 bg-white/80 dark:bg-zinc-800/80
                           backdrop-blur-xs flex items-center justify-center z-10 cursor-wait"
                role="status"
                aria-label="Loading"
                style={{ pointerEvents: 'all' }}
                onClick={(e) => e.stopPropagation()}
              >
                <Loader2 className="h-8 w-8 animate-spin text-sky-600" />
              </div>
            )}
          </div>

          {/* Footer */}
          {renderFooter() !== null && (
            <div className="flex gap-2 justify-end px-6 py-3 border-t border-zinc-200 dark:border-zinc-700">
              {renderFooter()}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
